"use client";

import { motion } from "framer-motion";
import { Star, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import type { Product } from "@/types/product";
import { useCart } from "@/context/CartContext";
import { useState } from "react";
import { Flame } from "lucide-react";

interface ProductCardProps {
  product: Product;
  index?: number;
  onAddToCart?: (product: Product) => void;
}

export function ProductCard({
  product,
  index = 0,
  onAddToCart,
}: ProductCardProps) {
  const { addToCart } = useCart();
  const [isAdded, setIsAdded] = useState(false);

  const [selectedVariations, setSelectedVariations] = useState<
    Record<string, string>
  >(() => {
    const initial: Record<string, string> = {};
    if (product.variations) {
      product.variations.forEach((v) => {
        if (v.options && v.options.length > 0) {
          // Pre-select the first available option.
          const firstAvailableOption = v.options.find((option) => option.inStock);
          if (firstAvailableOption) initial[v.name] = firstAvailableOption.name;
        }
      });
    }
    return initial;
  });

  const calculateFinalPrice = () => {
    if (product.variations && Object.keys(selectedVariations).length > 0) {
      let currentPrice = product.price;
      for (const [vName, oName] of Object.entries(selectedVariations)) {
        const variation = product.variations.find((v) => v.name === vName);
        if (variation) {
          const selectedOption = variation.options.find(
            (opt) => opt.name === oName,
          );
          if (selectedOption && selectedOption.price > 0) {
            currentPrice = selectedOption.price;
          }
        }
      }
      return currentPrice;
    }
    return product.price;
  };

  const finalPrice = calculateFinalPrice();

  const discountRatio =
    product.onSale && product.originalPrice && product.price > 0
      ? product.originalPrice / product.price
      : 1;

  const getOptionOriginalPrice = (optionPrice: number) => {
    return Math.round(optionPrice * discountRatio);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!product.inStock) return;

    // Check if required variations are selected and available.
    if (product.variations && product.variations.length > 0) {
      const requiredVariations = product.variations.filter((v) => v.required);
      const missing = requiredVariations.find(
        (v) => !selectedVariations[v.name],
      );
      if (missing) {
        return;
      }
      const unavailableSelection = product.variations.some((variation) => {
        const optionName = selectedVariations[variation.name];
        return optionName && !variation.options.find((option) => option.name === optionName)?.inStock;
      });
      if (unavailableSelection) return;
    }

    addToCart({
      id: product.id,
      name: product.name,
      price: finalPrice,
      image: product.image,
      description: product.description,
      productId: product.id,
      variations:
        Object.keys(selectedVariations).length > 0
          ? selectedVariations
          : undefined,
    } as any);

    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);

    if (onAddToCart) {
      onAddToCart(product);
    }
  };

  const hasVariations = product.variations && product.variations.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      className="h-full"
    >
      <Card className="group overflow-hidden h-full flex flex-col hover:shadow-xl transition-all duration-300 border-border/50 pt-0">
        {/* Image Container */}
        <div className="relative h-40 overflow-hidden bg-card">
          {product.badge && (
            <Badge className="absolute top-3 left-3 z-10 bg-primary text-primary-foreground">
              {product.badge}
            </Badge>
          )}
          {product.onSale && (
            <Badge
              variant="destructive"
              className="absolute top-3 right-3 z-10"
            >
              Sale
            </Badge>
          )}
          {!product.inStock && (
            <Badge
              variant="destructive"
              className="absolute bottom-3 left-3 z-10"
            >
              Out of Stock
            </Badge>
          )}
          <OptimizedImage
            src={product.image}
            alt={product.name}
            width={400}
            height={400}
            placeholder="blur"
            priority={index < 4}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
        </div>

        {/* Content */}
        <div className="flex-1 px-6 pt-4 pb-0 flex flex-col">
          <div className="block mb-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="text-xl font-semibold line-clamp-1 group-hover:text-primary transition-colors text-[#333]">
                {product.name}
              </h3>
              {product.rating && (
                <div className="flex items-center gap-1 shrink-0">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm font-medium">{product.rating}</span>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-2">
              {product.description}
            </p>
          </div>

          {/* Variations */}
          {hasVariations && (
            <div className="flex-1 flex flex-col gap-4 mb-4">
              {product.variations!.map((variation) => {
                const isSize = variation.name.toLowerCase().includes("size");
                const isSpice = variation.name.toLowerCase().includes("spice");

                return (
                  <div key={variation.id} className="flex flex-col gap-2">
                    <label className="text-sm text-muted-foreground">
                      {isSize
                        ? "Select size:"
                        : isSpice
                          ? "Spice level:"
                          : variation.name}
                    </label>

                    {isSize ? (
                      <div className="flex flex-col gap-1.5">
                        {variation.options.map((option) => {
                          const isSelected =
                            selectedVariations[variation.name] === option.name;
                          const optPrice =
                            option.price > 0 ? option.price : product.price;
                          const optOrigPrice = getOptionOriginalPrice(optPrice);

                          return (
                            <div
                              key={option.id}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedVariations((prev) => ({
                                  ...prev,
                                  [variation.name]: option.name,
                                }));
                              }}
                              className={`flex justify-between items-center p-2.5 rounded-lg cursor-pointer text-sm transition-colors ${
                                isSelected
                                  ? "bg-[#f8eef0] text-black"
                                  : "hover:bg-muted/50"
                              }`}
                            >
                              <span
                                className={
                                  isSelected
                                    ? "font-medium"
                                    : "text-muted-foreground"
                                }
                              >
                                {option.name}
                              </span>
                              <div className="flex items-center gap-2">
                                {product.onSale && optOrigPrice > optPrice && (
                                  <span
                                    className={
                                      isSelected
                                        ? "text-gray-500 line-through text-xs"
                                        : "text-muted-foreground line-through text-xs"
                                    }
                                  >
                                    ₦{optOrigPrice.toLocaleString()}
                                  </span>
                                )}
                                <span
                                  className={`font-semibold ${isSelected ? "text-primary" : ""}`}
                                >
                                  ₦{optPrice.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : isSpice ? (
                      <div className="flex flex-wrap gap-2">
                        {variation.options.map((option) => {
                          const isSelected =
                            selectedVariations[variation.name] === option.name;
                          const isNotSpicy = option.name
                            .toLowerCase()
                            .includes("not");
                          const isVerySpicy = option.name
                            .toLowerCase()
                            .includes("very");

                          return (
                            <div
                              key={option.id}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedVariations((prev) => ({
                                  ...prev,
                                  [variation.name]: option.name,
                                }));
                              }}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md cursor-pointer text-sm transition-colors ${
                                isSelected
                                  ? "bg-[#f8eef0] text-black font-medium"
                                  : "bg-muted/30 text-muted-foreground hover:bg-muted/50"
                              }`}
                            >
                              <Flame
                                className={`w-3.5 h-3.5 ${
                                  isNotSpicy
                                    ? "text-green-500"
                                    : isVerySpicy
                                      ? "text-red-500"
                                      : "text-orange-400"
                                }`}
                              />
                              {option.name}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {variation.options.map((option) => {
                          const isSelected =
                            selectedVariations[variation.name] === option.name;
                          return (
                            <div
                              key={option.id}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedVariations((prev) => ({
                                  ...prev,
                                  [variation.name]: option.name,
                                }));
                              }}
                              className={`px-3 py-1.5 rounded-md cursor-pointer text-sm transition-colors ${
                                isSelected
                                  ? "bg-[#f8eef0] text-primary font-medium"
                                  : "bg-muted/30 text-muted-foreground hover:bg-muted/50"
                              }`}
                            >
                              {option.name}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <CardFooter className="flex items-center justify-between p-6 pt-4 mt-auto">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              {product.onSale &&
                getOptionOriginalPrice(finalPrice) > finalPrice && (
                  <span className="text-sm text-muted-foreground line-through">
                    ₦{getOptionOriginalPrice(finalPrice).toLocaleString()}
                  </span>
                )}
              <span className="text-xl font-bold text-primary">
                ₦{finalPrice.toLocaleString()}
              </span>
              {product.onSale &&
                getOptionOriginalPrice(finalPrice) > finalPrice && (
                  <span className="bg-green-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded ml-1">
                    {Math.round(
                      ((getOptionOriginalPrice(finalPrice) - finalPrice) /
                        getOptionOriginalPrice(finalPrice)) *
                        100,
                    )}
                    % OFF
                  </span>
                )}
            </div>
          </div>
          {!product.inStock ? (
            <Button size="sm" variant="outline" disabled className="shrink-0">
              Out of Stock
            </Button>
          ) : (
            <Button
              onClick={handleAddToCart}
              size="sm"
              className="shrink-0 group/btn bg-primary hover:bg-primary/90 text-white rounded-md px-5"
              disabled={isAdded || (product.variations || []).some((variation) => {
                const optionName = selectedVariations[variation.name];
                return optionName && !variation.options.find((option) => option.name === optionName)?.inStock;
              })}
            >
              <Plus className="h-4 w-4 mr-1" />
              {isAdded ? "Added!" : "Add"}
            </Button>
          )}
        </CardFooter>
      </Card>
    </motion.div>
  );
}
